import {
  MeasurementUnits,
  TeamTemplateSize,
  TeamTemplateSizeInput,
  TemplateType
} from "state/entities/teamTemplateSizes/teamTemplateSizesActions";

export const MEASUREMENT_UNIT_OPTIONS: MeasurementUnits[] = ["px", "mm", "in"];

export const deriveTemplateType = (unit: MeasurementUnits): TemplateType =>
  unit === "px" ? "image" : "print";

export const NAME_MAX_LENGTH = 255;
export const MIN_DIMENSION_PX = 1;
export const MAX_DIMENSION_PX = 100_000;

const DPI = 96;
const MM_PER_INCH = 25.4;

const UNIT_TO_PX: Record<MeasurementUnits, number> = {
  px: 1,
  in: DPI,
  mm: DPI / MM_PER_INCH
};

export const toPixels = (value: number, unit: MeasurementUnits): number => {
  if (!Number.isFinite(value)) return 0;
  if (unit === "px") return Math.round(value);
  return Math.round(value * UNIT_TO_PX[unit]);
};

export const fromPixels = (pixels: number, unit: MeasurementUnits): number => {
  if (!Number.isFinite(pixels)) return 0;
  if (unit === "px") return pixels;
  const raw = pixels / UNIT_TO_PX[unit];
  // Snap to the nearest integer when it round-trips to the same stored
  // pixel value — otherwise integer user input (e.g. 1500mm) would display
  // as 1499.89 after the px round-trip.
  const asInt = Math.round(raw);
  if (toPixels(asInt, unit) === pixels) return asInt;
  return Math.round(raw * 100) / 100;
};

export interface SizeFormState {
  name: string;
  width: string;
  height: string;
  measurementUnits: MeasurementUnits;
  active: boolean;
  doubleSided: boolean;
}

export interface SizeFormErrors {
  name?: string;
  width?: string;
  height?: string;
}

export const emptySizeFormState: SizeFormState = {
  name: "",
  width: "",
  height: "",
  measurementUnits: "px",
  active: true,
  doubleSided: false
};

export const sizeToFormState = (size: TeamTemplateSize): SizeFormState => ({
  name: size.name,
  width: String(fromPixels(size.width, size.measurementUnits)),
  height: String(fromPixels(size.height, size.measurementUnits)),
  measurementUnits: size.measurementUnits,
  active: size.active,
  doubleSided: size.doubleSided
});

export const validateSizeForm = (form: SizeFormState): SizeFormErrors => {
  const errors: SizeFormErrors = {};

  const name = form.name.trim();
  if (!name) {
    errors.name = "Enter a size name.";
  } else if (name.length > NAME_MAX_LENGTH) {
    errors.name = `Name must be ${NAME_MAX_LENGTH} characters or fewer.`;
  }

  const widthError = validateDimension(
    form.width,
    form.measurementUnits,
    "Width"
  );
  if (widthError) errors.width = widthError;

  const heightError = validateDimension(
    form.height,
    form.measurementUnits,
    "Height"
  );
  if (heightError) errors.height = heightError;

  return errors;
};

const validateDimension = (
  raw: string,
  unit: MeasurementUnits,
  label: "Width" | "Height"
): string | undefined => {
  const num = Number(raw);
  if (!raw || !Number.isFinite(num) || num <= 0) {
    return `Enter a ${label.toLowerCase()} greater than 0.`;
  }
  // Reject sub-pixel px entries — toPixels silently rounds them (e.g. 0.5 → 1),
  // causing what the user sees to diverge from what the API stores.
  if (unit === "px" && !Number.isInteger(num)) {
    return `${label} must be a whole number of pixels.`;
  }
  const px = toPixels(num, unit);
  if (px < MIN_DIMENSION_PX || px > MAX_DIMENSION_PX) {
    return `${label} must be between ${MIN_DIMENSION_PX} and ${MAX_DIMENSION_PX} pixels.`;
  }
  return undefined;
};

export const isSizeFormValid = (errors: SizeFormErrors): boolean =>
  Object.keys(errors).length === 0;

export const buildSizeInput = (form: SizeFormState): TeamTemplateSizeInput => ({
  name: form.name.trim(),
  width: toPixels(Number(form.width), form.measurementUnits),
  height: toPixels(Number(form.height), form.measurementUnits),
  measurementUnits: form.measurementUnits,
  templateType: deriveTemplateType(form.measurementUnits),
  active: form.active,
  doubleSided: form.doubleSided
});
